import { Controller } from "@hotwired/stimulus";
import { onAnimationEnd } from "@theme/utilities";

class ProductGalleryController extends Controller {
  connect() {
    this.swiperThumb = this.initThumbSwiper();

    this.mainSwiper = this.initMainSwiper();

    this.zoomThumbSwiper = this.initZoomThumbSwiper();

    this.zoomSwiper = this.initZoomSwiper();

    this.dialogComponent = document.querySelector("dialog-component");

    this.initZoomLogics();
  }

  disconnect() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy();
    }

    if (this.swiperThumb) {
      this.swiperThumb.destroy();
    }

    if (this.zoomThumbSwiper) {
      this.zoomThumbSwiper.destroy();
    }

    if (this.zoomSwiper) {
      this.zoomSwiper.destroy();
    }
  }

  initMainSwiper() {
    let thumbs = {};

    if (this.swiperThumb) {
      thumbs = {
        swiper: this.swiperThumb,
        slideThumbActiveClass: "tw:swiper-slide-thumb-active",
      };
    }

    return new Swiper(this.element, {
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      slidesPerView: 1,
      thumbs,
    });
  }

  initThumbSwiper() {
    const CLASS_NAME = ".swiper-thumb";
    if (document.querySelector(CLASS_NAME)) {
      return new Swiper(CLASS_NAME, {
        spaceBetween: 10,
        slidesPerView: "auto",
        freeMode: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
      });
    }
  }

  initZoomThumbSwiper() {
    const CLASS_NAME = ".zoom-thumbs-swiper";
    if (document.querySelector(CLASS_NAME)) {
      return new Swiper(CLASS_NAME, {
        spaceBetween: 8,
        slidesPerView: "auto",
        watchSlidesProgress: true,
        breakpoints: {
          0: {
            direction: "horizontal",
          },
          768: {
            direction: "vertical",
          },
        },
      });
    }
  }

  initZoomSwiper() {
    let thumbs = {};

    if (this.zoomThumbSwiper) {
      thumbs = {
        swiper: this.zoomThumbSwiper,
        slideThumbActiveClass: "tw:swiper-slide-thumb-active",
      };
    }

    return new Swiper(".zoom-main-swiper", {
      spaceBetween: 10,
      thumbs,
    });
  }

  initZoomLogics() {
    if (this.swiperThumb) {
      this.swiperThumb.slides.forEach((slide, index) => {
        slide.addEventListener("mouseenter", () => {
          this.mainSwiper.slideTo(index);
        });
      });
    }

    document.querySelectorAll("[data-product-image]").forEach((img, index) => {
      img.addEventListener("click", () => {
        this.dialogComponent.showDialog();
        this.zoomSwiper.slideTo(index, 0);

        // ONLY MOBILE
        if (window.innerWidth < 768) {
          const hint = document.getElementById("mobile-zoom-hint");

          setTimeout(() => {
            hint.classList.remove("tw:opacity-100");
            hint.classList.add("tw:opacity-0");

            onAnimationEnd(hint, () => {
              hint.classList.add("tw:hidden");
            });
          }, 3000);
        }
      });
    });

    document.querySelectorAll(".zoom-image").forEach((img) => {
      let zoomed = false;
      let startX = 0,
        startY = 0;
      let currentX = 0,
        currentY = 0;

      if (this.zoomThumbSwiper) {
        this.zoomThumbSwiper.slides.forEach((slide, index) => {
          slide.addEventListener("click", () => {
            document.querySelectorAll(".zoom-image").forEach((img) => {
              img.classList.remove("is-zoomed");
              img.style.transform = "";
            });

            currentX = 0;
            currentY = 0;
            zoomed = false;

            this.zoomSwiper.slideTo(index);

            this.zoomSwiper.allowTouchMove = true;
          });
        });
      }

      // DOUBLE TAP MOBILE
      let lastTap = 0;
      img.addEventListener("touchend", (e) => {
        const now = Date.now();
        const delta = now - lastTap;

        if (delta < 300 && delta > 0) {
          zoomed = !zoomed;
          img.classList.toggle("is-zoomed", zoomed);

          this.zoomSwiper.allowTouchMove = !zoomed;

          if (!zoomed) {
            img.style.transform = "";
            currentX = 0;
            currentY = 0;
          }
        }
        lastTap = now;
      });

      // PAN TOUCH
      img.addEventListener("touchstart", (e) => {
        if (!zoomed) return;
        const touch = e.touches[0];
        startX = touch.clientX - currentX;
        startY = touch.clientY - currentY;
      });

      img.addEventListener("touchmove", (e) => {
        if (!zoomed) return;
        e.preventDefault();

        const touch = e.touches[0];
        currentX = touch.clientX - startX;
        currentY = touch.clientY - startY;

        const scale = 2.5;
        const rect = img.getBoundingClientRect();
        const maxX = (rect.width * (scale - 1)) / 2;
        const maxY = (rect.height * (scale - 1)) / 2;

        currentX = Math.min(maxX, Math.max(-maxX, currentX));
        currentY = Math.min(maxY, Math.max(-maxY, currentY));

        img.style.transform = `scale(${scale}) translate(${currentX / scale}px, ${currentY / scale}px)`;
      });

      // ZOOM DESKTOP CLICK
      img.addEventListener("click", (e) => {
        if (window.innerWidth >= 768) {
          zoomed = !zoomed;
          img.classList.toggle("is-zoomed", zoomed);
          this.zoomSwiper.allowTouchMove = !zoomed;

          if (!zoomed) {
            img.style.transform = "";
            currentX = 0;
            currentY = 0;
          }
        }
      });

      // MOUSE MOVE PAN DESKTOP
      img.addEventListener("mousemove", (e) => {
        if (!zoomed || window.innerWidth < 768) return;

        const rect = img.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;

        img.style.transformOrigin = `${x}% ${y}%`;
      });
    });
  }
}

Stimulus.register("productGallery", ProductGalleryController);
